/**
 * PhotographyBookingFlow - Booking for Photography & Videography services
 * Package-based booking with portfolio preview
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar } from '../ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { 
  Calendar as CalendarIcon, Clock, Camera, Check, ChevronRight, Loader2,
  MapPin, Image, Video, Package
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIME_OPTIONS = [
  '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00', 
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
];

const EVENT_TYPES = [
  { value: 'weddings', label: 'Wedding' },
  { value: 'birthday', label: 'Birthday Party' },
  { value: 'corporate', label: 'Corporate Event' },
  { value: 'engagement', label: 'Engagement / Pre-wedding' },
  { value: 'portrait', label: 'Portrait / Headshots' },
  { value: 'product', label: 'Product Photography' },
  { value: 'other', label: 'Other' }
];

export default function PhotographyBookingFlow({ open, onClose, provider, onBookingComplete }) {
  const [step, setStep] = useState(1);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [eventType, setEventType] = useState('');
  const [location, setLocation] = useState('');
  const [includeVideo, setIncludeVideo] = useState(false);
  const [specialRequests, setSpecialRequests] = useState('');
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const packages = provider?.packages || [
    { id: 'basic', name: 'Basic Coverage', duration: '4 hours', price: 45000, includes: ['100 edited photos', 'Online gallery', '1 photographer'] },
    { id: 'standard', name: 'Standard Coverage', duration: '8 hours', price: 85000, includes: ['300 edited photos', 'Online gallery', 'USB delivery', '1 photographer'] },
    { id: 'premium', name: 'Premium Coverage', duration: 'Full day', price: 150000, includes: ['500+ edited photos', 'Online gallery', 'Album (30 pages)', '2 photographers', 'Drone shots'] }
  ];
  
  const addOns = provider?.add_ons || [
    { id: 'video', name: 'Videography', price: 50000 },
    { id: 'drone', name: 'Drone Coverage', price: 25000 },
    { id: 'album', name: 'Premium Album', price: 35000 },
    { id: 'same_day', name: 'Same-Day Edit (30 photos)', price: 15000 }
  ];
  
  const currency = provider?.currency || 'Rs';

  const calculateTotal = () => {
    let total = selectedPackage?.price || 0;
    selectedAddOns.forEach(aoId => {
      const addOn = addOns.find(a => a.id === aoId);
      if (addOn) total += addOn.price;
    });
    return total;
  };

  const calculateEndTime = () => {
    if (!startTime || !selectedPackage) return '';
    const durationHours = selectedPackage.duration === 'Full day' ? 10 : parseInt(selectedPackage.duration);
    const startHour = parseInt(startTime.split(':')[0]);
    const endHour = Math.min(startHour + durationHours, 23);
    return `${endHour.toString().padStart(2, '0')}:00`;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const bookingData = {
        provider_id: provider?.id,
        event_date: format(selectedDate, 'yyyy-MM-dd'),
        start_time: startTime,
        end_time: calculateEndTime(),
        package_id: selectedPackage?.id,
        event_type: eventType,
        location: location,
        add_ons: selectedAddOns,
        include_video: includeVideo,
        notes: specialRequests,
        contact: contactInfo
      };

      const response = await fetch(`${API_URL}/api/events/bookings/photography`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bookingData)
      });

      if (response.ok) {
        toast.success('Photography booking request sent! They will confirm within 24 hours.');
        onBookingComplete?.();
        onClose();
      } else {
        throw new Error('Booking failed');
      }
    } catch (error) {
      toast.error('Failed to complete booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Select Package</h3>
            
            <div className="space-y-3">
              {packages.map(pkg => (
                <button
                  key={pkg.id}
                  onClick={() => setSelectedPackage(pkg)}
                  className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                    selectedPackage?.id === pkg.id
                      ? 'border-pink-500 bg-pink-50'
                      : 'border-gray-200 hover:border-pink-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-lg">{pkg.name}</p>
                      <p className="text-sm text-gray-600">{pkg.duration}</p>
                      {pkg.includes && (
                        <ul className="mt-2 text-sm text-gray-500">
                          {pkg.includes.map((item, i) => (
                            <li key={i} className="flex items-center gap-1">
                              <Check className="w-3 h-3 text-green-500" /> {item}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-xl text-pink-600">{currency} {pkg.price?.toLocaleString()}</p>
                      {selectedPackage?.id === pkg.id && (
                        <Check className="w-6 h-6 text-pink-600 ml-auto mt-2" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Add-ons */}
            {addOns.length > 0 && (
              <div className="pt-4 border-t">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Add-ons</label>
                <div className="grid grid-cols-2 gap-2">
                  {addOns.map(addOn => (
                    <label key={addOn.id} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <Checkbox
                        checked={selectedAddOns.includes(addOn.id)}
                        onCheckedChange={(checked) => {
                          setSelectedAddOns(prev => 
                            checked ? [...prev, addOn.id] : prev.filter(id => id !== addOn.id)
                          );
                        }}
                      />
                      <div className="flex-1">
                        <span className="text-sm">{addOn.name}</span>
                      </div>
                      <span className="text-sm font-bold text-pink-600">+{currency} {addOn.price?.toLocaleString()}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Event Details</h3>
            
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-2 block">Event Date</label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date < new Date() || date > addDays(new Date(), 365)}
                  className="rounded-md border"
                />
              </div>
              
              <div className="flex-1 space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Start Time</label>
                  <Select value={startTime} onValueChange={setStartTime}>
                    <SelectTrigger>
                      <Clock className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map(time => (
                        <SelectItem key={time} value={time}>{time}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Event Type</label>
                  <Select value={eventType} onValueChange={setEventType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700">Event Location</label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Venue address"
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-800">Contact & Confirm</h3>
            
            <div className="space-y-3">
              <Input
                placeholder="Your full name *"
                value={contactInfo.name}
                onChange={(e) => setContactInfo(c => ({ ...c, name: e.target.value }))}
              />
              <Input
                placeholder="Phone number *"
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(c => ({ ...c, phone: e.target.value }))}
              />
              <Input
                type="email"
                placeholder="Email address *"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(c => ({ ...c, email: e.target.value }))}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700">Special Requests</label>
              <Textarea
                value={specialRequests}
                onChange={(e) => setSpecialRequests(e.target.value)}
                placeholder="Any specific shots, styles, or requirements..."
                rows={3}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              <h4 className="font-medium">Booking Summary</h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-600">Package</span>
                  <span className="font-medium">{selectedPackage?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date</span>
                  <span>{selectedDate && format(selectedDate, 'MMM d, yyyy')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time</span>
                  <span>{startTime} - {calculateEndTime()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Event</span>
                  <span className="capitalize">{eventType}</span>
                </div>
                {selectedAddOns.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Add-ons</span>
                    <span>{selectedAddOns.length} selected</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2 flex justify-between font-bold">
                  <span>Total</span>
                  <span className="text-pink-600">{currency} {calculateTotal().toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-pink-700">
                  <span>Deposit (30%)</span>
                  <span>{currency} {(calculateTotal() * 0.3).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <Camera className="w-4 h-4 inline mr-2" />
                This is a booking request. The photographer will confirm availability within 24 hours.
              </p>
            </div>
          </div>
        );
    }
  };

  if (!provider) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-5 h-5 text-pink-600" />
            Book Photographer
          </DialogTitle>
          <DialogDescription>{provider.provider_name}</DialogDescription>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3].map((s) => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s < step ? 'bg-pink-600 text-white' :
                s === step ? 'bg-pink-100 text-pink-700 border-2 border-pink-600' :
                'bg-gray-100 text-gray-400'
              }`}>
                {s < step ? <Check className="w-4 h-4" /> : s}
              </div>
              {s < 3 && <div className={`flex-1 h-1 ${s < step ? 'bg-pink-600' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
        </div>

        {renderStep()}

        <div className="flex justify-between pt-4 border-t">
          {step > 1 ? (
            <Button variant="outline" onClick={() => setStep(s => s - 1)}>Back</Button>
          ) : <div />}
          
          {step < 3 ? (
            <Button 
              onClick={() => setStep(s => s + 1)}
              disabled={
                (step === 1 && !selectedPackage) ||
                (step === 2 && (!selectedDate || !startTime || !eventType || !location))
              }
              className="bg-pink-600 hover:bg-pink-700"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit}
              disabled={loading || !contactInfo.name || !contactInfo.phone || !contactInfo.email}
              className="bg-pink-600 hover:bg-pink-700"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Send Booking Request
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
